import React, {
  BaseSyntheticEvent,
  WheelEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { Input } from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import {
  ListItemProps,
  MultiSelectChangeEvent,
  DropDownListChangeEvent,
  DropDownList,
  ComboBoxChangeEvent,
} from "@progress/kendo-react-dropdowns";
import moment from "moment";
import "moment/min/locales";
import {
  AuditPeriodFilterObj,
  CallFilter,
  CallPeriodFilterObj,
  FilterObject,
} from "../types/filters";
import { Topic } from "../types/topic";
import { TinyUser, User } from "../types/user";
import {
  ColorCategory,
  Platform,
  Sentiment,
  CallStatus,
  CallDirection,
  CallType,
} from "../types/master-data";
import { Popup } from "@progress/kendo-react-popup";
import { Link } from "react-router-dom";
import useLocale from "../hooks/useLocale";
import { Dictionary } from "../types/Dictionary";
import useMasterData from "../hooks/useMasterData";
import useAuth from "../hooks/useAuth";
import usersService from "../services/users.service";
import CustomUserGridInfoLocalizationProp from "./custom/grid/CustomUserGridInfoLocalizationProp";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import { Loader } from "@progress/kendo-react-indicators";
import { Dates, getDateBasedOnFilterType } from "../utils/FilterDrawerUtil";
import { EvaluationRange } from "../types/master-data/EvaluationRange";
import CustomComboBox from "./custom/form/CustomComboBox";
import CustomMultiSelect from "./custom/form/CustomMultiSelect";
import { SelectFilterObject } from "../types/filters/SelectFilterObject";
import {
  TinyUserGroup,
  UserGroupRelation,
} from "../types/user/UserGroupRelation";
import useTranslation from "../hooks/useTranslation";
import { EntityKey } from "../types/master-data/EntityKey";
import { CallResult } from "../types/call";
import { ReasonCodeInstance } from "../types/call/CallResultReasonCode";
import { CustomCalendar } from "./custom/form/CustomCalendar";
import customerService from "../services/customer.service";
import { Customer } from "../types/customer";
import { TranscriptionEntityType } from "../types/master-data/TranscriptionEntityType";
import { CallMode } from "../types/master-data/CallMode";
import userGroupsService from "../services/usergroups.service";

interface IDurationTypeDropdown extends EntityKey {
  id: number;
  title: string;
  field: string;
}

type CallFilterProps = {
  userMasterData?: User;
  callFilterData?: CallFilter;
  handleCallDataFilter: (callFilterData: CallFilter) => void;
  handleFilterDialog: () => void;
};

const CallFilterDrawer: React.FC<CallFilterProps> = (props) => {
  const trans = useTranslation("callsFilter");
  const masterData = useMasterData();
  const localeCtx = useLocale();
  const auth = useAuth();
  const periodFilterMenu = [
    {
      id: 1,
      title: "Day",
      displayTitle: "Day",
      translationLabelKey: "DayFilterLabel",
      filters: [
        {
          id: 1,
          title: "Today",
          translationLabelKey: "TodayFilterLabel",
          active: false,
        },
        {
          id: 2,
          title: "Yesterday",
          translationLabelKey: "YesterdayFilterLabel",
          active: false,
        },
        {
          id: 3,
          title: "Last 7 Days",
          translationLabelKey: "Last7DaysFilterLabel",
          active: false,
        },
        {
          id: 4,
          title: "Last 30 Days",
          translationLabelKey: "Last30DaysFilterLabel",
          active: false,
        },
      ],
      refObj: useRef<any>(),
      showFilters: false,
    },
    {
      id: 2,
      title: "Week",
      displayTitle: "Week",
      translationLabelKey: "WeekFilterLabel",
      filters: [
        {
          id: 1,
          title: "This Week",
          translationLabelKey: "ThisWeekFilterLabel",
          active: false,
        },
        {
          id: 2,
          title: "Last Week",
          translationLabelKey: "LastWeekFilterLabel",
          active: false,
        },
      ],
      refObj: useRef<any>(),
      showFilters: false,
    },
    {
      id: 3,
      title: "Month",
      displayTitle: "Month",
      translationLabelKey: "MonthFilterLabel",
      filters: [
        {
          id: 1,
          title: "This Month",
          translationLabelKey: "ThisMonthFilterLabel",
          active: false,
        },
        {
          id: 2,
          title: "Last Month",
          translationLabelKey: "LastMonthFilterLabel",
          active: false,
        },
      ],
      refObj: useRef<any>(),
      showFilters: false,
    },
    {
      id: 4,
      title: "Custom",
      displayTitle: "Custom",
      translationLabelKey: "CustomFilterLabel",
      showFilters: false,
    },
  ];

  const sentimentNoneFilter: Sentiment = {
    id: 0,
    name: "None",
    localizationKey: "NoneText",
    localizationValue: trans.fetchLabelKeyTranslation("NoneText", "None"),
  };
  const sentimentFilter: Sentiment[] = masterData?.data?.sentiments
    ? [...masterData.data.sentiments, sentimentNoneFilter]
    : [sentimentNoneFilter];
  const flagNoneFilter: ColorCategory = {
    id: 0,
    name: "None",
    hexCode: "None",
    localizationKey: "NoneText",
    localizationValue: trans.fetchLabelKeyTranslation("NoneText", "None"),
  };
  const flagFilter: ColorCategory[] = masterData?.data?.colorCategories
    ? [...masterData.data.colorCategories, flagNoneFilter]
    : [flagNoneFilter];

  const durationTypes: [
    IDurationTypeDropdown,
    IDurationTypeDropdown,
    IDurationTypeDropdown
  ] = [
    { id: 1, title: "seconds", field: "sec", localizationKey: "Seconds" },
    { id: 2, title: "minutes", field: "min", localizationKey: "Minutes" },
    { id: 3, title: "hours", field: "hr", localizationKey: "Hours" },
  ];

  const [selectObject, setSelectObject] = useState<SelectFilterObject[]>([
    {
      id: "1",
      field: "Yes",
      displayField: "Yes",
      displayFieldKey: "TranscriptionFilterYes",
    },
    {
      id: "2",
      field: "No",
      displayField: "No",
      displayFieldKey: "TranscriptionFilterNo",
    },
  ]);

  const [periodFilters, setPeriodFilters] =
    useState<CallPeriodFilterObj[]>(periodFilterMenu);
  const [periodType, setPeriodType] = useState<CallPeriodFilterObj>(
    periodFilters[0]
  );
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [startTime, setStartTime] = useState<Date | null>(
    new Date(2000, 1, 1, 0, 0, 0, 0)
  );
  const [endTime, setEndTime] = useState<Date | null>(
    new Date(2000, 1, 1, 23, 59, 59, 0)
  );
  const [firstparty, setFirstParty] = useState<UserGroupRelation[]>([]);
  const [firstpartyGroup, setFirstPartyGroup] = useState<UserGroupRelation[]>([]);
  const [otherParty, setOtherParty] = useState<string | undefined>(undefined);
  const [platform, setPlatform] = useState<Platform[] | undefined>(undefined);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [callResults, setCallResults] = useState<CallResult[]>([]);
  const [reasonCodes, setReasonCodes] = useState<ReasonCodeInstance[]>([]);
  const [flag, setFlag] = useState<ColorCategory[] | undefined>(undefined);
  const [evaluated, setEvaluated] = useState<EvaluationRange[]>([]);
  const [callDirection, setCallDirection] = useState<
    CallDirection[] | undefined
  >(undefined);
  const [callType, setCallType] = useState<CallType[] | undefined>(undefined);
  const [sentiment, setSentiment] = useState<Sentiment[] | undefined>(
    undefined
  );
  const [callStatus, setCallStatus] = useState<CallStatus[] | undefined>(
    undefined
  );
  const [transcriptionFilter, setTranscriptionFilter] = useState<
    SelectFilterObject | undefined
  >(undefined);
  const [minDuration, setMinDuration] = useState<number | undefined>(undefined);
  const [maxDuration, setMaxDuration] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string>("");
  const [durationTypeValueForMin, setDurationTypeValueForMin] = useState(
    durationTypes[0]
  );
  const [durationTypeValueForMax, setDurationTypeValueForMax] = useState(
    durationTypes[0]
  );
  const [userGroupRelation, setUserGroupRelation] = useState<
    UserGroupRelation[]
  >([]);
  const [retentionFilter, setRetentionFilter] = useState<
    SelectFilterObject | undefined
  >(undefined);
  const [transcriptionEntityFilter, setTranscriptionEntityFilter] = useState<
    TranscriptionEntityType[] | undefined
  >(undefined);
  const [callMode, setCallMode] = useState<CallMode[] | undefined>(undefined);
  const [customerDetails, setCustomerDetails] = useState<Customer>();
  const [allGroups, setAllGroups] = useState<TinyUserGroup[] | undefined>(undefined);

  const translatePeriodFiltersMenu = (
    updatedTranslations: Dictionary<string> | undefined
  ): CallPeriodFilterObj[] => {
    const updatedMenu = periodFilters;
    if (updatedTranslations) {
      updatedMenu.forEach((p) => {
        p.displayTitle = updatedTranslations[p.translationLabelKey] ?? p.title;
        if (p.filters) {
          p.filters.forEach((f) => {
            f.title = updatedTranslations[f.translationLabelKey] ?? f.title;
          });
        }
      });
    }
    return updatedMenu;
  };

  const translateSelectMenu = (
    updatedTranslations: Dictionary<string> | undefined
  ) => {
    let transSelect: SelectFilterObject[] = [];
    transSelect = selectObject;

    if (updatedTranslations) {
      transSelect[0].displayField =
        updatedTranslations[transSelect[0].displayFieldKey] ?? "Yes";
      transSelect[1].displayField =
        updatedTranslations[transSelect[1].displayFieldKey] ?? "No";
    } else {
      transSelect[0].displayField = "Yes";
      transSelect[1].displayField = "No";
    }

    setSelectObject(transSelect);
  };

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations["callsFilter"]
    ) {
      trans.fetchTranslations("callsFilter");
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    const getCustomerDetails = async () => {
      const custDetails = await customerService.getCustomerDetailsById(
        auth?.tokenPayload?.CustomerId ?? 0
      );
      setCustomerDetails(custDetails);
    };
    getCustomerDetails();
  }, [auth?.tokenPayload?.CustomerId]);

  const loadAllGroups = async () => {
    var groups = await userGroupsService.getTinyUserGroups();
    setAllGroups(groups);    
  };

  useEffect(()=>{
    if(props.userMasterData?.recordingNetworkAccessibilty?.id === 4){
      loadAllGroups();
    }
  },[props.userMasterData]);

  const setFilterValues = () => {
    if (props.callFilterData) {
      if (props.callFilterData.periodType && props.callFilterData.filterType) {
        // Reset and Activate Select Period Filter
        const initalPeriodFilterMenu: CallPeriodFilterObj[] =
          translatePeriodFiltersMenu(trans.translations);
        const updatedPeriodFilters: CallPeriodFilterObj[] = [];
        initalPeriodFilterMenu.forEach((p) => {
          if (p.id === props.callFilterData?.periodType) {
            if (p.filters) {
              p.filters.forEach((f) => {
                if (f.id === props.callFilterData?.filterType) {
                  f.active = true;
                } else {
                  f.active = false;
                }
              });
            }
          } else {
            p.filters = p.filters?.map((f) => {
              f.active = false;
              return f;
            });
          }
          updatedPeriodFilters.push(p);
        });
        setPeriodFilters(updatedPeriodFilters);
        const period =
          periodFilters[
            periodFilters.findIndex(
              (el) => el.id === props.callFilterData?.periodType
            )
          ];
        period.filters?.forEach((f) => {
          if (f.id === props.callFilterData?.filterType) {
            f.active = true;
          }
        });
        setPeriodType(period);
      } else if (props.callFilterData.periodType) {
        // Reset the period filters
        const initialFilters = translatePeriodFiltersMenu(trans.translations);
        setPeriodFilters(
          initialFilters.map((p) => {
            p.filters?.forEach((f) => {
              f.active = false;
            });
            return p;
          })
        );

        setPeriodType(
          periodFilters[
            periodFilters.findIndex(
              (el) => el.id === props.callFilterData?.periodType
            )
          ]
        );
      }

      if (props.callFilterData?.startDate !== undefined) {
        setStartDate(new Date(props.callFilterData?.startDate));
      }
      if (props.callFilterData?.endDate !== undefined) {
        setEndDate(new Date(props.callFilterData?.endDate));
      }
      if (props.callFilterData?.startTime !== undefined) {
        setStartTime(timeStringToDate(props.callFilterData?.startTime));
      }
      if (props.callFilterData?.endTime !== undefined) {
        var et = timeStringToDate(props.callFilterData?.endTime);
        setEndTime(et);
      }
      if (props.userMasterData) {
        const users: UserGroupRelation[] =
          props.userMasterData?.recordingNetwork
            ?.filter((user: TinyUser) =>
              props.callFilterData?.users?.includes(user.id)
            )
            .map((user: TinyUser) => {
              const newData: UserGroupRelation = {
                id: user.id,
                isGroup: false,
                filterField: user.email,
                filterFieldName: user.firstName + " " + user.lastName,
                user: user,
              };
              return newData;
            });

        const userPlayback = allGroups ?? props.userMasterData?.associatedGroups?.playbackNetwork;

        const groups: UserGroupRelation[] =
          userPlayback
            .filter((group: TinyUserGroup) =>
              props.callFilterData?.usergroups?.includes(group.id)
            )
            .map((group: TinyUserGroup) => {
              const newData: UserGroupRelation = {
                id: group.id,
                isGroup: true,
                filterField: group.name,
                filterFieldName: group.name,
                group: group,
              };
              return newData;
            });
        setFirstParty(users);
        setFirstPartyGroup(groups);
      }

      setOtherParty(props.callFilterData?.otherParties?.join(";"));

      if (props.callFilterData.durationTypeForMin === "min") {
        setMinDuration(
          props.callFilterData?.minDuration
            ? Number(props.callFilterData.minDuration) / 60
            : undefined
        );
      } else if (props.callFilterData.durationTypeForMin === "hr") {
        setMinDuration(
          props.callFilterData?.minDuration
            ? Number(props.callFilterData.minDuration) / 3600
            : undefined
        );
      } else {
        setMinDuration(
          props.callFilterData?.minDuration
            ? Number(props.callFilterData.minDuration)
            : undefined
        );
      }

      if (props.callFilterData.durationTypeForMax === "min") {
        setMaxDuration(
          props.callFilterData?.maxDuration
            ? Number(props.callFilterData.maxDuration) / 60
            : undefined
        );
      } else if (props.callFilterData.durationTypeForMax === "hr") {
        setMaxDuration(
          props.callFilterData?.maxDuration
            ? Number(props.callFilterData.maxDuration) / 3600
            : undefined
        );
      } else {
        setMaxDuration(
          props.callFilterData?.maxDuration
            ? Number(props.callFilterData.maxDuration)
            : undefined
        );
      }

      if (props?.callFilterData?.durationTypeForMin) {
        const dType = durationTypes.find(
          (d: IDurationTypeDropdown) =>
            d.field === props.callFilterData?.durationTypeForMin
        );
        if (dType) {
          setDurationTypeValueForMin(dType);
        }
      }

      if (props?.callFilterData?.durationTypeForMax) {
        const dType = durationTypes.find(
          (d: IDurationTypeDropdown) =>
            d.field === props.callFilterData?.durationTypeForMax
        );
        if (dType) {
          setDurationTypeValueForMax(dType);
        }
      }

      setPlatform(
        masterData?.data?.platforms?.filter((p) =>
          props.callFilterData?.platforms?.includes(p.name)
        )
      );
      setFlag(
        flagFilter.filter((c) =>
          props.callFilterData?.categories?.includes(c.name)
        )
      );
      if (props.userMasterData?.topics) {
        setTopics(
          props.userMasterData?.topics?.filter((t) =>
            props.callFilterData?.topics?.includes(t.topicName)
          )
        );
      }
      if (props.userMasterData?.callResults) {
        setCallResults(
          props.userMasterData.callResults.filter((c) =>
            props.callFilterData?.callResults?.includes(c.text)
          )
        );
      }
      if (props.userMasterData?.reasonCodes) {
        setReasonCodes(
          props.userMasterData.reasonCodes.filter((r) =>
            props.callFilterData?.reasonCodes?.includes(r.name)
          )
        );
      }
      setSentiment(
        sentimentFilter.filter((s) =>
          props.callFilterData?.sentiments?.includes(s.name)
        )
      );
      setCallStatus(
        masterData?.data?.callStatuses?.filter((s) =>
          props.callFilterData?.callStatuses?.includes(s.name)
        )
      );

      const evals: EvaluationRange[] = [];
      masterData?.data?.evaluationRange.forEach((fEval: EvaluationRange) => {
        if (props.callFilterData?.evaluations?.includes(fEval.name)) {
          evals.push(fEval);
        }
      });
      setEvaluated(evals);

      setCallDirection(
        masterData?.data?.callDirections.filter((c) =>
          props.callFilterData?.callDirections?.includes(c.name)
        )
      );

      setCallType(
        masterData?.data?.callTypes.filter((c) =>
          props.callFilterData?.callTypes?.includes(c.name)
        )
      );

      if (props.callFilterData.transcribed) {
        if (props.callFilterData.transcribed == "Yes")
          setTranscriptionFilter(selectObject[0]);
        else setTranscriptionFilter(selectObject[1]);
      }

      if (props.callFilterData.retained) {
        if (props.callFilterData.retained == "Yes")
          setRetentionFilter(selectObject[0]);
        else setRetentionFilter(selectObject[1]);
      }

      setTranscriptionEntityFilter(
        masterData?.data?.transcriptionEntityTypes.filter((t) =>
          props.callFilterData?.transcriptionEntities?.includes(t.name)
        )
      );

      setCallMode(
        masterData?.data?.callModes.filter((c) =>
          props.callFilterData?.callModes?.includes(c.name))
      );
    }
  };

  useEffect(() => {
    setFilterValues();
  }, [props.callFilterData, allGroups]);

  useEffect(() => {
    translateSelectMenu(trans.translations);
  }, [trans.translations]);


  useEffect(() => {
    if (props.userMasterData) {
      const users: TinyUser[] = props.userMasterData?.recordingNetwork ?? [];
      const groups: TinyUserGroup[] = allGroups ??
        props.userMasterData?.associatedGroups?.playbackNetwork;

      const sortedUsers: TinyUser[] = users.sort((a: TinyUser, b: TinyUser) => {
        if (a.email > b.email) return 1;
        if (a.email < b.email) return -1;
        return 0;
      });
      const sortedGroups: TinyUserGroup[] = groups.sort(
        (a: TinyUserGroup, b: TinyUserGroup) => {
          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
          return 0;
        }
      );
      const userRelationData: UserGroupRelation[] = sortedUsers.map(
        (user: TinyUser) => {
          const data: UserGroupRelation = {
            id: user.id,
            filterField: user.email,
            filterFieldName: user.firstName + " " + user.lastName,
            isGroup: false,
            user: user,
          };
          return data;
        }
      );
      const groupRelationData: UserGroupRelation[] = sortedGroups.map(
        (group: TinyUserGroup) => {
          const data: UserGroupRelation = {
            id: group.id,
            filterField: group.name,
            filterFieldName: group.name,
            isGroup: true,
            group: group,
          };
          return data;
        }
      );
      setUserGroupRelation(userRelationData.concat(groupRelationData));
    }
  }, [props.userMasterData, allGroups]);

  const handlePeriodSelection = (
    period: CallPeriodFilterObj,
    filter: { id: number; title: string; active: boolean }
  ) => {  
    // Reset and Activate Select Period Filter
    const updatedPeriodFilters:AuditPeriodFilterObj[]=[];
    const translatedPeriodFilters: AuditPeriodFilterObj[] =
      translatePeriodFiltersMenu(trans.translations);
      translatedPeriodFilters.forEach((p) => {
      if (p.id === period.id) {
        if (p.filters) {
          p.filters.forEach((f) => {
            if (f.id === filter.id) {
              f.active = true;
            }
          });
          p.showFilters = false;
        }
        updatedPeriodFilters.push(p);
      }else{
        const findDefaultPeriod=periodFilterMenu.find((pp:AuditPeriodFilterObj)=>pp.id===p.id);
        if(findDefaultPeriod){
          updatedPeriodFilters.push(findDefaultPeriod);
        }
      }
    });
    setPeriodFilters(updatedPeriodFilters);
    const getFilterBasesDates: Dates = getDateBasedOnFilterType(
      period.id ?? 0,
      filter.id ?? 0
    );
    setStartDate(getFilterBasesDates.tempStartDate);
    setEndDate(getFilterBasesDates.tempEndDate);
    // Set current period type
    period.filters?.forEach((f) => {
      if (f.id === filter.id) {
        f.active = true;
      } else {
        f.active = false;
      }
    });
    setPeriodType(period);
  };

  const timeStringToDate = (time: string) => {
    let _time = time.split(":").map((el) => parseInt(el));
    let dt = new Date(2000, 1, 1, _time[0], _time[1], _time[2]);
    return dt;
  };

  const handleCallDataFilter = () => {
    let tempMinDuration, tempMaxDuration;
    if (minDuration === undefined) tempMinDuration = undefined;
    if (maxDuration === undefined) tempMaxDuration = undefined;
    if (minDuration !== undefined) {
      if (durationTypeValueForMin.field === "min") {
        tempMinDuration = (Number(minDuration) * 60).toString();
      } else if (durationTypeValueForMin.field === "hr") {
        tempMinDuration = (Number(minDuration) * 60 * 60).toString();
      } else {
        tempMinDuration = minDuration.toString();
      }
    }
    if (maxDuration !== undefined) {
      if (durationTypeValueForMax.field === "min") {
        tempMaxDuration = (Number(maxDuration) * 60).toString();
      } else if (durationTypeValueForMax.field === "hr") {
        tempMaxDuration = (Number(maxDuration) * 60 * 60).toString();
      } else {
        tempMaxDuration = maxDuration.toString();
      }
    }
    var tempStartDate = startDate;
    var tempEndDate = endDate;
    tempStartDate?.setHours(0, 0, 0);
    tempEndDate?.setHours(23, 59, 59);

    const filterType = periodType.filters?.find((f) => f.active === true);
    let internalParties: number[] = [];
    let externalParties: string[] = [];
    if (otherParty && otherParty.length > 0) {
      const participants: string[] = otherParty.split(";").map((x) => x.trim());
      participants.forEach((element) => {
        const recordedUserId = masterData?.users.find(
          (x) => x?.email === element
        )?.id;
        if (recordedUserId) {
          internalParties.push(recordedUserId);
        }
        externalParties.push(element);
      });
    }

    var filterValues: CallFilter = {
      startDate: moment(tempStartDate).format("YYYY-MM-DD HH:mm:ss"),
      endDate: moment(tempEndDate).format("YYYY-MM-DD HH:mm:ss"),
      startTime: moment(startTime).format("HH:mm:ss"),
      endTime: moment(endTime).format("HH:mm:ss"),
      periodType: periodType?.id,
      filterType: filterType?.id,
      users: firstparty
        .filter((u: UserGroupRelation) => !u.isGroup)
        .map((u: UserGroupRelation) => u.id),
      usergroups: firstpartyGroup
        .filter((u: UserGroupRelation) => u.isGroup)
        .map((u: UserGroupRelation) => u.id),
      otherParties: externalParties,
      internalCallParties: internalParties,
      platforms: platform?.map((p) => p.name),
      categories: flag?.map((f) => f.name),
      topics: topics?.map((t) => t.topicName),
      callResults: callResults?.map((c) => c.text),
      reasonCodes: reasonCodes?.map((r) => r.name),
      sentiments: sentiment?.map((s) => s.name),
      callStatuses: callStatus?.map((s) => s.name),
      evaluations: evaluated?.map((e: EvaluationRange) => e.name),
      callDirections:
        callDirection !== undefined
          ? callDirection?.map((cd) => cd.name)
          : undefined,
      callTypes:
        callType !== undefined ? callType?.map((ct) => ct.name) : undefined,
      minDuration: tempMinDuration,
      maxDuration: tempMaxDuration,
      durationTypeForMin: durationTypeValueForMin.field,
      durationTypeForMax: durationTypeValueForMax.field,
      transcribed: transcriptionFilter?.field,
      retained: retentionFilter?.field,
      transcriptionEntities: transcriptionEntityFilter?.map((t) => t.name),
      callModes: callMode?.map((c)=>c.name)
    };
    console.log(filterValues);
    props.handleCallDataFilter(filterValues);
  };

  const periodFilterClickHandler = (period: CallPeriodFilterObj) => {
    if (
      period.title.toLocaleLowerCase() === "custom" &&
      periodType?.title.toLocaleLowerCase() !== "custom"
    ) {
      setPeriodType(period);
      setStartDate(new Date());
      setEndDate(new Date());
      setPeriodFilters(translatePeriodFiltersMenu(trans.translations));
    } else {
      setPeriodFilters((prev) => {
        return prev.map((p) => {
          if (p.id === period.id) {
            p.showFilters = !p.showFilters;
          } else {
            p.showFilters = false;
          }
          return p;
        });
      });
    }
  };

  const itemrenderTopics = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const checkedclass = itemProps.selected ? "liChecked" : "";
    const itemChildren = (
      <span>
        <i
          className={`bi-${
            itemProps.dataItem.isPublic ? "people" : "lock-fill"
          } m-r-6`}
        ></i>
        <span>{itemProps.dataItem.topicName}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const itemrenderCallResults = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const checkedclass = itemProps.selected ? "liChecked" : "";
    const itemChildren = (
      <span>
        <span>{itemProps.dataItem.text}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const itemrenderReasonCodes = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const checkedclass = itemProps.selected ? "liChecked" : "";
    const itemChildren = (
      <span>
        <span>{itemProps.dataItem.name}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const itemRenderUser = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const checkedclass = itemProps.selected ? "liChecked" : "";

    const itemChildren = (
      <span className={checkedclass}>
        {itemProps.dataItem.isGroup ? (
          <div className="mixDataCol">
            <div>
              <div className="tblUsr">
                <div className="topUsrAreaPic">
                  <div className="topUsrAreaPic-i">
                    <div className="contact-list-icon bg-thistle">
                      <div className="contact-list-icon-txt">
                        <i className="bi bi-people-fill"></i>
                      </div>
                    </div>
                  </div>
                  <div className="topUsrAreaPic-tx hoverTextFull">
                    <span
                      className="text-primary font-weight-semi cursor-default text-decoration-none float-left w-100 line-height-2 mx-td-spn viewFullWordHover"
                      title={itemProps.dataItem.group.name}
                    >
                      {itemProps.dataItem.group.name}
                    </span>
                    <span
                      className="text-muted fs-13 line-height-2 mx-td-spn viewFullWordHover"
                    >
                      <span className="fs-11">
                        <span className="p-r-15">
                          Admins: <b>{itemProps.dataItem.group.adminCount}</b>
                        </span>
                        <span>
                          Members: <b>{itemProps.dataItem.group.memberCount}</b>
                        </span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <CustomUserGridInfoLocalizationProp
            localizationText={trans.fetchLabelKeyTranslation(
                  "WarningIcon",
                  "Assign an ultimate license to this user to use auto QA."
            )}
            userInfo={itemProps.dataItem.user}
            selected={itemProps.selected}
          />
        )}
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const handleClearFilter = () => {
    const updatedPeriodFilters:AuditPeriodFilterObj[]=[];
    const translatedPeriodFilters: AuditPeriodFilterObj[] =
      translatePeriodFiltersMenu(trans.translations);
    translatedPeriodFilters.forEach((p) => {
      if (p.id === 1) {
        if (p.filters) {
          p.filters.forEach((f) => {
            if (f.id === 1) {
              f.active = true;
            }
          });
          p.showFilters = false;
        }
        updatedPeriodFilters.push(p);
      }else{
        const findDefaultPeriod=periodFilterMenu.find((pp:AuditPeriodFilterObj)=>pp.id===p.id);
        if(findDefaultPeriod){
          updatedPeriodFilters.push(findDefaultPeriod);
        }
      }
    });
    setPeriodFilters(updatedPeriodFilters);
    setPeriodType(updatedPeriodFilters[0]);
    setStartDate(new Date());
    setEndDate(new Date());
    setStartTime(new Date(2000, 1, 1, 0, 0, 0, 0));
    setEndTime(new Date(2000, 1, 1, 23, 59, 59, 0));
    setFirstParty([]);
    setFirstPartyGroup([]);
    setOtherParty("");
    setPlatform([]);
    setTopics([]);
    setCallResults([]);
    setReasonCodes([]);
    setFlag([]);
    setEvaluated([]);
    setCallDirection(undefined);
    setCallType(undefined);
    setSentiment([]);
    setCallStatus([]);
    setDurationTypeValueForMin({
      id: 1,
      title: "seconds",
      field: "sec",
      localizationKey: "seconds",
    });
    setDurationTypeValueForMax({
      id: 1,
      title: "seconds",
      field: "sec",
      localizationKey: "seconds",
    });
    setMinDuration(undefined);
    setMaxDuration(undefined);
    setError("");
    setTranscriptionFilter(undefined);
    setRetentionFilter(undefined);
    setTranscriptionEntityFilter([]);
    setCallMode([]);
  };

  const handleDurationType = (type: string, event: DropDownListChangeEvent) => {
    if (type === "min") {
      setDurationTypeValueForMin(event.target.value);
    } else if (type === "max") {
      setDurationTypeValueForMax(event.target.value);
    }
  };

  const maxDurationOnBlurEvent = () => {
    if (maxDuration !== undefined && minDuration !== undefined) {
      let maxVal = maxDuration,
        minVal = minDuration;
      if (durationTypeValueForMin.field === "min") {
        minVal = minDuration * 60;
      } else if (durationTypeValueForMin.field === "hr") {
        minVal = minDuration * 3600;
      }
      if (durationTypeValueForMax.field === "min") {
        maxVal = maxDuration * 60;
      } else if (durationTypeValueForMax.field === "hr") {
        maxVal = maxDuration * 3600;
      }
      if (maxVal < minVal) {
        setError("Max Duration cannot be less than Min duration");
      } else {
        setError("");
      }
    } else {
      setError("");
    }
  };

  useEffect(() => {
    if (periodFilters.some((p) => p.showFilters === true)) {
      const body = document.getElementById("root");
      const eventHandler = () => {
        setPeriodFilters((prev) => {
          return prev.map((p) => {
            p.showFilters = false;
            return p;
          });
        });
      };
      if (body != null) {
        body.addEventListener("click", eventHandler);
      }
      return () => body?.removeEventListener("click", eventHandler);
    }
  }, [periodFilters]);

  const durationTypeItemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const itemChildren = (
      <span key={itemProps.dataItem.id}>
        {trans.fetchLabelKeyTranslation(
          itemProps.dataItem.localizationKey ?? "",
          itemProps.dataItem.title
        )}
      </span>
    );

    return React.cloneElement(li, li.props, itemChildren);
  };

  const durationTypeValueRender = (
    element: React.ReactElement<HTMLSpanElement>,
    value: IDurationTypeDropdown
  ) => {
    if (!value) {
      return element;
    }
    const children = [
      <span key={value.id}>
        {trans.fetchLabelKeyTranslation(
          value.localizationKey ?? "",
          value.title
        )}
      </span>,
    ];
    return React.cloneElement(element, { ...element.props }, children);
  };

  return (
    <div className="float-left border-top-1 border-black-1 border-top-solid p-t-10 p-b-10 p-r-15 p-l-15 w-100">
      <div className="filterExpd w-100 float-left">
        <div className="filterMain m-b-5 w-100 float-left">
          <div className="filterTrak">
            <div className="row">
              <div className="col-md-12">
                <div className="filterTrak-h fs-15 font-weight-semi p-b-5">
                  {trans.fetchLabelKeyTranslation(
                    "PeriodFilterTitle",
                    "Date/Time"
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="filterTrak-btn">
                  <div className="filterTrakRow">
                    <ButtonGroup className="rdButtonFilter">
                      {periodFilters.map((period) => {
                        return (
                          <div key={period.id}>
                            <Button
                              togglable={true}
                              onClick={() => periodFilterClickHandler(period)}
                              selected={period.id === periodType.id}
                            >
                              <span ref={period.refObj}>
                                {period.filters?.some((p)=>p.active)?
                                  trans.fetchLabelKeyTranslation(period.filters?.find((p)=>p.active)?.translationLabelKey??period.translationLabelKey, period.filters?.find((p)=>p.active)?.title??period.displayTitle):
                                  trans.fetchLabelKeyTranslation(period.translationLabelKey, period.displayTitle)}
                              </span>
                              {period.filters && (
                                <i className="bi bi-chevron-down p-l-3 fs-14"></i>
                              )}
                            </Button>
                            {period.filters && (
                              <Popup
                                anchor={period.refObj?.current}
                                show={period.showFilters}
                                popupClass={"popup-content"}
                                style={{
                                  marginLeft: "-10px",
                                  marginTop: "10px",
                                }}
                              >
                                <ul
                                  className="dropMenu min-w-40 droplist-ul"
                                  style={{
                                    maxWidth: "180px",
                                    width: "120px",
                                    top: "0",
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                  }}
                                >
                                  {period.filters.map((filter) => {
                                    return (
                                      <li
                                        key={filter.id}
                                        className="droplist-li"
                                      >
                                        <Link
                                          to="#"
                                          className={`droplist-a ${
                                            filter.active ? "activeFilter" : ""
                                          }`}
                                          onClick={(e) =>
                                            handlePeriodSelection(
                                              period,
                                              filter
                                            )
                                          }
                                        >
                                          {trans.fetchLabelKeyTranslation(
                                            filter.translationLabelKey,
                                            filter.title
                                          )}
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </Popup>
                            )}
                          </div>
                        );
                      })}
                    </ButtonGroup>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="filterTrak-date p-t-1">
                  <div className="setSubData">
                    {periodType.title.toLocaleLowerCase() === "custom" && (
                      <div className="setSubTrk">
                        <div className="form-group">
                          <div className="rangeFld">
                            <div className="rangeFldTrk">
                              <div className="rangeInput">
                                <DatePicker
                                  label={trans.fetchLabelKeyTranslation(
                                    "StartDateFilterLabel",
                                    "Start Date"
                                  )}
                                  value={startDate}
                                  max={endDate !== null ? endDate : new Date()}
                                  onChange={(event) =>
                                    setStartDate(event.value)
                                  }
                                  format="dd/MM/yyyy"
                                  width="100%"
                                  calendar={CustomCalendar}
                                />
                              </div>
                            </div>
                            <div
                              className="rangeFldTrk"
                              style={{ paddingRight: "0" }}
                            >
                              <div className="rangeInput">
                                <DatePicker
                                  label={trans.fetchLabelKeyTranslation(
                                    "EndDateFilterLabel",
                                    "End Date"
                                  )}
                                  value={endDate}
                                  min={
                                    startDate !== null ? startDate : new Date()
                                  }
                                  max={new Date()}
                                  onChange={(event) => setEndDate(event.value)}
                                  format="dd/MM/yyyy"
                                  width="100%"
                                  calendar={CustomCalendar}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="setSubTrk">
                      <div className="form-group">
                        <div className="rangeFld">
                          <div className="rangeFldTrk">
                            <div className="rangeInput">
                              <TimePicker
                                label={trans.fetchLabelKeyTranslation(
                                  "StartTimeFilterLabel",
                                  "Start Time"
                                )}
                                format="HH:mm:ss"
                                value={startTime}
                                onChange={(event) => setStartTime(event.value)}
                                width="100%"
                              />
                            </div>
                          </div>
                          <div
                            className="rangeFldTrk"
                            style={{ paddingRight: "0" }}
                          >
                            <div className="rangeInput">
                              <TimePicker
                                label={trans.fetchLabelKeyTranslation(
                                  "EndTimeFilterLabel",
                                  "End Time"
                                )}
                                format="HH:mm:ss"
                                value={endTime}
                                onChange={(event) => setEndTime(event.value)}
                                width="100%"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-t-10">
              <div className="col-md-12">
                <div className="inrExpand bg-black-1">
                  <div className="expandableNav navTitleCenter expandBox">
                    <div className="panelbar-wrapper">
                      <PanelBar animation={true}>
                        <PanelBarItem
                          title={trans.fetchLabelKeyTranslation(
                            "ParticipantsFilterLabel",
                            "Participants"
                          )}
                          expanded={false}
                        >
                          <div className="inrExpandList float-left w-100 p-t-7 p-r-10 p-b-7 p-l-10">
                            <div className="float-left w-100">
                              <div className="inputList">
                                <div className="box border rounded p-3 m-b-10">
                                  <div className="inputRow p-b-10">
                                    <div className="inputLabel">
                                      {trans.fetchLabelKeyTranslation(
                                        "FirstPartyFilterLabel",
                                        "Recorded User"
                                      )}
                                    </div>
                                    <CustomMultiSelect
                                      data={userGroupRelation.filter(x => !x.isGroup)}
                                      onChange={(event: MultiSelectChangeEvent) =>
                                        setFirstParty([...event.value])
                                      }
                                      value={firstparty}
                                      dataItemKey="id"
                                      itemRender={itemRenderUser}
                                      textField="filterField"
                                      filterFields={[
                                        "filterField",
                                        "filterFieldName",
                                      ]}
                                      filtering={true}
                                      sorting={true}
                                      sortField="filterFieldName"
                                    />
                                  </div>
                                  <div className="or-devider m-b-10 text-center"><span>
                                    {trans.fetchLabelKeyTranslation(
                                        "RecordedUserGroupOR",
                                        "OR"
                                      )}
                                      </span></div>
                                  <div className="inputRow p-b-2">
                                    <div className="inputLabel">
                                      {trans.fetchLabelKeyTranslation(
                                        "RecordedUserGroupFilterLabel",
                                        "Recorded User Groups"
                                      )}
                                      </div>
                                    <CustomMultiSelect
                                      data={userGroupRelation.filter(x => x.isGroup )}
                                      onChange={(event: MultiSelectChangeEvent) =>
                                        setFirstPartyGroup([...event.value])
                                      }
                                      value={firstpartyGroup}
                                      dataItemKey="id"
                                      itemRender={itemRenderUser}
                                      textField="filterField"
                                      filterFields={[
                                        "filterField",
                                        "filterFieldName",
                                      ]}
                                      filtering={true}
                                      sorting={true}
                                      sortField="filterFieldName"
                                      disabled={userGroupRelation.filter(x => x.isGroup )?.length === 0}
                                    />
                                  {(userGroupRelation.filter(x => x.isGroup )?.length === 0) &&
                                  <Error>
                                    {trans.fetchLabelKeyTranslation("NoGroupsAssigned","No user groups assigned.")}
                                  </Error>}
                                  </div>
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "RemotePartyFilterLabel",
                                      "Participants"
                                    )}
                                  </div>
                                  <div className="multiselectBox">
                                    <Input
                                      style={{ height: "31px" }}
                                      value={otherParty}
                                      onChange={(e) => setOtherParty(e.value)}
                                      ariaDescribedBy={"otherPartyHint"}
                                    />
                                    <Hint id={"otherPartyHint"}>
                                      {trans.fetchLabelKeyTranslation(
                                        "RemotePartyFilterHint",
                                        "Separate multiple participants by a semicolon"
                                      )}
                                      (;).
                                    </Hint>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </PanelBarItem>
                      </PanelBar>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-t-15">
              <div className="col-md-12">
                <div className="inrExpand bg-black-1">
                  <div className="expandableNav navTitleCenter expandBox">
                    <div className="panelbar-wrapper">
                      <PanelBar animation={true}>
                        <PanelBarItem
                          title={trans.fetchLabelKeyTranslation(
                            "CallsFiltersFilterLabel",
                            "Calls"
                          )}
                          expanded={false}
                        >
                          <div className="inrExpandList float-left w-100 p-t-7 p-r-10 p-b-7 p-l-10">
                            <div className="float-left w-100">
                              <div className="inputList">
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "CallDirectionFilterLabel",
                                      "Call Direction"
                                    )}
                                  </div>
                                  <div className="multiselectBox">
                                    <CustomComboBox
                                      data={masterData?.data?.callDirections}
                                      onChange={(
                                        event: ComboBoxChangeEvent
                                      ) => {
                                        event.value !== null
                                          ? setCallDirection([event.value])
                                          : setCallDirection(undefined);
                                      }}
                                      value={
                                        callDirection !== undefined
                                          ? callDirection[0]
                                          : null
                                      }
                                      textField="localizationValue"
                                      dataItemKey="id"
                                      sorting={true}
                                      sortField={"localizationValue"}
                                    />
                                  </div>
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "CallTypeFilterLabel",
                                      "Call Relation"
                                    )}
                                  </div>
                                  <div className="multiselectBox">
                                    <CustomComboBox
                                      data={masterData?.data?.callTypes}
                                      onChange={(
                                        event: ComboBoxChangeEvent
                                      ) => {
                                        event.value !== null
                                          ? setCallType([event.value])
                                          : setCallType(undefined);
                                      }}
                                      value={
                                        callType !== undefined
                                          ? callType[0]
                                          : null
                                      }
                                      textField="localizationValue"
                                      dataItemKey="id"
                                      sorting={true}
                                      sortField={"localizationValue"}
                                    />
                                  </div>
                                </div>
                                {customerDetails?.platformDetails.some(
                                        (platform) =>
                                          platform.platform === "MsTeams"
                                      ) &&
                                  <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "CallModeFilterLabel",
                                      "Call Type"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={masterData?.data?.callModes}
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setCallMode([...event.value])
                                    }
                                    value={callMode}
                                    textField="localizationValue"
                                    dataItemKey="id"
                                    sorting={true}
                                    sortField={"localizationValue"}
                                  />
                                </div>}
                                <div className="inputRow p-b-10 remove-upperlower-spinner">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "DurationLabel",
                                      "Call Duration"
                                    )}
                                  </div>
                                  <div className="row p-b-10">
                                    <div className="col-8">
                                      <div className="position-relative">
                                        <Input
                                          type="number"
                                          style={{
                                            height: "31px",
                                          }}
                                          onBlur={maxDurationOnBlurEvent}
                                          value={
                                            minDuration !== undefined
                                              ? minDuration
                                              : ""
                                          }
                                          onKeyDown={(evt) =>
                                            (evt.key === "e" ||
                                              evt.key === ".") &&
                                            evt.preventDefault()
                                          }
                                          onWheel={(e: BaseSyntheticEvent) =>
                                            e.target.blur()
                                          }
                                          onChange={(e) => {
                                            if (Number(e.value) > 0) {
                                              setMinDuration(Number(e.value));
                                            } else {
                                              setMinDuration(undefined);
                                            }
                                          }}
                                          ariaDescribedBy={"otherPartyHint"}
                                          placeholder={trans.fetchLabelKeyTranslation(
                                            "MinDurationPlaceholder",
                                            "Min. Duration"
                                          )}
                                        />
                                        {/* {minDuration !== undefined && (
                                          <i
                                            onClick={() => {
                                              setMinDuration(undefined);
                                              setError("");
                                            }}
                                            style={{
                                              position: "absolute",
                                              right: "10px",
                                              top: "6px",
                                              cursor: "pointer",
                                            }}
                                            className="bi bi-x"
                                          ></i>
                                        )} */}
                                      </div>
                                    </div>
                                    <div className="col-4 pl-0">
                                      <DropDownList
                                        dataItemKey="id"
                                        data={durationTypes}
                                        value={durationTypeValueForMin}
                                        onChange={(
                                          e: DropDownListChangeEvent
                                        ) => handleDurationType("min", e)}
                                        onBlur={maxDurationOnBlurEvent}
                                        style={{
                                          border: "none",
                                          height: "30px",
                                        }}
                                        textField="title"
                                        itemRender={durationTypeItemRender}
                                        valueRender={durationTypeValueRender}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col-8">
                                      <div className="position-relative">
                                        <Input
                                          type="number"
                                          style={{
                                            height: "31px",
                                          }}
                                          value={
                                            maxDuration !== undefined
                                              ? maxDuration
                                              : ""
                                          }
                                          onBlur={maxDurationOnBlurEvent}
                                          onWheel={(e: BaseSyntheticEvent) =>
                                            e.target.blur()
                                          }
                                          onChange={(e) => {
                                            if (Number(e.value) > 0) {
                                              setMaxDuration(Number(e.value));
                                            } else {
                                              setMaxDuration(undefined);
                                            }
                                          }}
                                          onKeyDown={(evt) =>
                                            (evt.key === "e" ||
                                              evt.key === ".") &&
                                            evt.preventDefault()
                                          }
                                          ariaDescribedBy={"otherPartyHint"}
                                          placeholder={trans.fetchLabelKeyTranslation(
                                            "MaxDurationPlaceholder",
                                            "Max Duration"
                                          )}
                                        />
                                        {/* {maxDuration !== undefined && (
                                          <i
                                            onClick={() => {
                                              setMaxDuration(undefined);
                                              setError("");
                                            }}
                                            style={{
                                              position: "absolute",
                                              right: "10px",
                                              top: "6px",
                                              cursor: "pointer",
                                            }}
                                            className="bi bi-x"
                                          ></i>
                                        )} */}
                                      </div>
                                    </div>
                                    <div className="col-4 pl-0">
                                      <DropDownList
                                        dataItemKey="id"
                                        data={durationTypes}
                                        value={durationTypeValueForMax}
                                        onChange={(
                                          e: DropDownListChangeEvent
                                        ) => handleDurationType("max", e)}
                                        onBlur={maxDurationOnBlurEvent}
                                        style={{
                                          border: "none",
                                          height: "30px",
                                        }}
                                        textField="title"
                                        itemRender={durationTypeItemRender}
                                        valueRender={durationTypeValueRender}
                                      />
                                    </div>
                                  </div>
                                  <Error>{error}</Error>
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "PlatformFilterLabel",
                                      "Platform"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={masterData?.data?.platforms.filter((x)=>{ return customerDetails?.platformDetails.find((y)=>{ return y.platform === x.name})})}
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setPlatform([...event.value])
                                    }
                                    value={platform}
                                    textField="localizationValue"
                                    dataItemKey="id"
                                    sorting={true}
                                    sortField={"localizationValue"}
                                  />
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "CallStatusFilterLabel",
                                      "Call Status"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={
                                      customerDetails?.platformDetails.some(
                                        (platform) =>
                                          platform.platform === "MsTeams"
                                      )
                                        ? masterData?.data?.callStatuses
                                        : masterData?.data?.callStatuses.filter(
                                            (x) =>
                                              x.name.toLocaleLowerCase() !=
                                              "discarded"
                                          )
                                    }
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setCallStatus([...event.value])
                                    }
                                    value={callStatus}
                                    textField="localizationValue"
                                    dataItemKey="id"
                                    sorting={true}
                                    sortField={"localizationValue"}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </PanelBarItem>
                      </PanelBar>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-t-15">
              <div className="col-md-12">
                <div className="inrExpand bg-black-1">
                  <div className="expandableNav navTitleCenter expandBox">
                    <div className="panelbar-wrapper">
                      <PanelBar animation={true}>
                        <PanelBarItem
                          title={trans.fetchLabelKeyTranslation(
                            "EvaluationFiltersFilterLabel",
                            "Evaluation"
                          )}
                          expanded={false}
                        >
                          <div className="inrExpandList float-left w-100 p-t-7 p-r-10 p-b-7 p-l-10">
                            <div className="float-left w-100">
                              <div className="inputList">
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "FlagFilterLabel",
                                      "Flag"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={flagFilter}
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setFlag([...event.value])
                                    }
                                    value={flag}
                                    textField="localizationValue"
                                    dataItemKey="id"
                                    sorting={true}
                                    sortField={"localizationValue"}
                                  />
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "EvalFilterLabel",
                                      "Evaluation Score"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={[
                                      masterData?.data?.evaluationRange[2],
                                      masterData?.data?.evaluationRange[1],
                                      masterData?.data?.evaluationRange[0],
                                      masterData?.data?.evaluationRange[3],
                                    ]}
                                    value={evaluated}
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setEvaluated([...event.value])
                                    }
                                    textField="localizationValue"
                                    dataItemKey="id"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </PanelBarItem>
                      </PanelBar>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-t-15">
              <div className="col-md-12">
                <div className="inrExpand bg-black-1">
                  <div className="expandableNav navTitleCenter expandBox">
                    <div className="panelbar-wrapper">
                      <PanelBar animation={true}>
                        <PanelBarItem
                          title={trans.fetchLabelKeyTranslation(
                            "ConversationAnalyticsLabel",
                            "Conversation Analytics"
                          )}
                          expanded={false}
                        >
                          <div className="inrExpandList float-left w-100 p-t-7 p-r-10 p-b-7 p-l-10">
                            <div className="float-left w-100">
                              <div className="inputList">
                                {auth?.checkTopicAvailibility() && (
                                  <div className="inputRow p-b-10">
                                    <div className="inputLabel">
                                      {trans.fetchLabelKeyTranslation(
                                        "TopicsFilterLabel",
                                        "Topics"
                                      )}
                                    </div>
                                    <CustomMultiSelect
                                      data={props.userMasterData?.topics ?? []}
                                      value={topics}
                                      onChange={(
                                        event: MultiSelectChangeEvent
                                      ) => setTopics([...event.value])}
                                      textField="topicName"
                                      itemRender={itemrenderTopics}
                                      dataItemKey="topicName"
                                      filtering={true}
                                      sorting={true}
                                      sortField={"topicName"}
                                    />
                                  </div>
                                )}
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "SentimentFilterLabel",
                                      "Sentiment"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={sentimentFilter}
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setSentiment([...event.value])
                                    }
                                    value={sentiment}
                                    textField="localizationValue"
                                    dataItemKey="id"
                                    sorting={true}
                                    sortField={"localizationValue"}
                                  />
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "TranscriptionFilterLabel",
                                      "Transcription"
                                    )}
                                  </div>
                                  <div className="multiselectBox">
                                    <CustomComboBox
                                      data={selectObject}
                                      onChange={(
                                        event: ComboBoxChangeEvent
                                      ) => {
                                        event.value !== null
                                          ? setTranscriptionFilter(event.value)
                                          : setTranscriptionFilter(undefined);
                                      }}
                                      value={
                                        transcriptionFilter !== undefined
                                          ? transcriptionFilter
                                          : null
                                      }
                                      textField="displayField"
                                      dataItemKey="id"
                                    />
                                  </div>
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "TranscriptionEntityFilterLabel",
                                      "Redaction Category"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={masterData?.data?.transcriptionEntityTypes}
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setTranscriptionEntityFilter([...event.value])
                                    }
                                    value={transcriptionEntityFilter}
                                    textField="localizationValue"
                                    dataItemKey="id"
                                    sorting={true}
                                    sortField={"localizationValue"}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </PanelBarItem>
                      </PanelBar>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-t-15">
              <div className="col-md-12">
                <div className="inrExpand bg-black-1">
                  <div className="expandableNav navTitleCenter expandBox">
                    <div className="panelbar-wrapper">
                      <PanelBar animation={true}>
                        <PanelBarItem
                          title={trans.fetchLabelKeyTranslation(
                            "OtherFiltersFilterLabel",
                            "Other"
                          )}
                          expanded={false}
                        >
                          <div className="inrExpandList float-left w-100 p-t-7 p-r-10 p-b-7 p-l-10">
                            <div className="float-left w-100">
                              <div className="inputList">
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "OutcomesFilterLabel",
                                      "Outcomes"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={
                                      props.userMasterData?.callResults ?? []
                                    }
                                    value={callResults}
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setCallResults([...event.value])
                                    }
                                    textField="text"
                                    itemRender={itemrenderCallResults}
                                    dataItemKey="text"
                                    filtering={true}
                                    sorting={true}
                                    sortField={"text"}
                                  />
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "ReasonCodesFilterLabel",
                                      "Reason Code"
                                    )}
                                  </div>
                                  <CustomMultiSelect
                                    data={
                                      props.userMasterData?.reasonCodes ?? []
                                    }
                                    value={reasonCodes}
                                    onChange={(event: MultiSelectChangeEvent) =>
                                      setReasonCodes([...event.value])
                                    }
                                    textField="name"
                                    itemRender={itemrenderReasonCodes}
                                    dataItemKey="name"
                                    filtering={true}
                                    sorting={true}
                                    sortField={"name"}
                                  />
                                </div>
                                <div className="inputRow p-b-10">
                                  <div className="inputLabel">
                                    {trans.fetchLabelKeyTranslation(
                                      "LegalHoldFilterLabel",
                                      "Legal Hold"
                                    )}
                                  </div>
                                  <div className="multiselectBox">
                                    <CustomComboBox
                                      data={selectObject}
                                      onChange={(
                                        event: ComboBoxChangeEvent
                                      ) => {
                                        event.value !== null
                                          ? setRetentionFilter(event.value)
                                          : setRetentionFilter(undefined);
                                      }}
                                      value={
                                        retentionFilter !== undefined
                                          ? retentionFilter
                                          : null
                                      }
                                      textField="displayField"
                                      dataItemKey="id"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </PanelBarItem>
                      </PanelBar>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row m-t-15">
              <div className="col-md-12">
                <div className="d-flex justify-content-end">
                  {/* <Button className="btn bg-black-5 m-r-3"
                    onClick={props.handleFilterDialog}
                  >
                    Close
                  </Button> */}
                  <Button
                    className="btn bg-primary text-white m-r-5"
                    onClick={handleClearFilter}
                  >
                    {trans.fetchLabelKeyTranslation("ResetBtnLabel", "Reset")}
                  </Button>
                  <Button
                    disabled={error === "" ? false : true}
                    className="btn bg-primary text-white"
                    onClick={handleCallDataFilter}
                  >
                    {trans.fetchLabelKeyTranslation("FilterBtnLabel", "Filter")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallFilterDrawer;
